// Normalisers between the serde-wasm-bindgen shapes `fk-sim-wasm` emits/accepts and the TS
// mirror in ./types.ts. Pure functions; covered by adapt.test.ts against `demo_encounter`.
import { catalogueItem, fightUse, isCatalogue, itemAbilityId } from '../items/catalogue';
import type {
  ActionArgs, Aim, AreaHit, Cause, DamageSourceGroup, ConflictFlag, Direction, Effect, EnemyDeclaration, Intent, IntentRef, IntentState, LegalityVerdict,
  ProjectedBoard, ProjectedEffect, ProjectedUnit, ResolutionEntry, ResolutionLog, Slot, Snapshot, Stage, StatusKind, StatusTag, Terrain,
  Tile, TileData, UnitState, InteractVerb, BagView, StashKind, StashView, FixtureView, LockKindName, MachineName, TrapKindName, FieldEnv, FieldLine, Survey, ModifierNote, SwayNote, NoteLine, MindNote, CardCount, FocusNote, FocusPart, FogView, FogVeil, FogEyes, FogMark, GoalsView, GoalView, LossView, AimRange, AimWhy, FallenView, StandAt,
  BoonName, CrawlView, ExploreView, ExploreGift, PendingCard, ReserveView, UnitMark, FlankKind,
  MatterPatch, ThingView, SurfaceName, CloudName, ThingKind,
  ActKind, CurioName, SnareView, SnareTrigger, SnareState,
} from './types';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
/** Variant name of a serde enum value (`"Open"` or `{ Tile: {...} }`). */
export const variant = (v: unknown): string => (typeof v === 'string' ? v : isObj(v) ? Object.keys(v)[0] ?? '' : '');
const payload = (v: unknown): unknown => (isObj(v) ? v[variant(v)] : undefined);
/** docs/103: fk_sim `Flank` (`"Backstab"` / `"Misdirection"`). */
const flankKind = (v: unknown): FlankKind => (variant(v) === 'Misdirection' ? 'misdirection' : 'backstab');
const num = (v: unknown): number => (typeof v === 'bigint' ? Number(v) : typeof v === 'number' ? v : Number(v ?? 0));

// ---- hashes -------------------------------------------------------------------------------

export function bytesToHex(b: ArrayLike<number> | undefined | null): string {
  if (!b) return '';
  let s = '';
  for (let i = 0; i < b.length; i++) s += (b[i]! & 0xff).toString(16).padStart(2, '0');
  return s;
}
export function hexToBytes(hex: string): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < hex.length; i += 2) out.push(parseInt(hex.slice(i, i + 2), 16));
  return out;
}

// ---- slots / refs / args ------------------------------------------------------------------

export function slotFromWasm(s: unknown): Slot { return s === 'Two' ? 2 : s === 'Bonus' ? 'B' : 1; }
export function slotToWasm(s: Slot): 'One' | 'Two' | 'Bonus' { return s === 1 ? 'One' : s === 2 ? 'Two' : 'Bonus'; }
export function slotToU8(s: Slot): number { return s === 1 ? 0 : s === 2 ? 1 : 2; }
export function slotFromU8(n: number): Slot { return n === 0 ? 1 : n === 1 ? 2 : 'B'; }

export function stateFromWasm(s: unknown): IntentState {
  switch (s) { case 'Local': return 'local'; case 'Committed': return 'committed'; case 'Rejected': return 'rejected'; default: return 'provisional'; }
}

export function refFromWasm(r: Obj): IntentRef {
  const ref: IntentRef = { heroId: num(r['hero_id'] ?? r['heroId']), slot: slotFromWasm(r['slot']), state: stateFromWasm(r['state']) };
  const id = r['intent_id'] ?? r['intentId'];
  if (id !== undefined && id !== null) ref.intentId = num(id);
  return ref;
}

export function argsFromWasm(a: unknown): ActionArgs {
  const k = variant(a);
  const p = payload(a) as Obj | undefined;
  switch (k) {
    case 'None': return { kind: 'none' };
    case 'Tile': return { kind: 'tile', tile: tileOf(p) };
    case 'Unit': return { kind: 'unit', unit: num(payload(a)) };
    case 'UnitDir': return { kind: 'unitDir', unit: num(p?.['target']), dir: String(p?.['dir']) as Direction };
    case 'Line': return { kind: 'line', from: tileOf(p?.['from'] as Obj), dir: String(p?.['dir']) as Direction };
    case 'Interact': return { kind: 'interact', fixture: num(p?.['fixture']), verb: String(p?.['verb']) as InteractVerb };
    case 'Item': { const out: ActionArgs = { kind: 'item', item: num(p?.['item']) }; const t = p?.['target']; if (t !== null && t !== undefined) out.target = num(t); return out; }
    default: return { kind: 'raw', value: a };
  }
}

export function argsToWasm(a: ActionArgs): unknown {
  switch (a.kind) {
    case 'none': return 'None';
    case 'tile': return { Tile: { x: a.tile.x, y: a.tile.y } };
    case 'unit': return { Unit: a.unit };
    case 'unitDir': return { UnitDir: { target: a.unit, dir: a.dir } };
    case 'direction': return { Line: { from: { x: 0, y: 0 }, dir: a.dir } };
    case 'line': return { Line: { from: { x: a.from.x, y: a.from.y }, dir: a.dir } };
    case 'interact': return { Interact: { fixture: a.fixture, verb: a.verb } };
    case 'item': return { Item: { item: a.item, target: a.target ?? null } };
    case 'raw': return a.value;
  }
}

export function intentFromWasm(w: Obj): Intent {
  return {
    heroId: num(w['hero_id']), unit: num(w['unit']), slot: slotFromWasm(w['slot']), action: num(w['action']),
    args: argsFromWasm(w['args']), snapshotHash: bytesToHex(w['snapshot_hash'] as number[]), revision: num(w['revision']),
    substituted: Boolean(w['substituted']),
  };
}

export function intentToWasm(i: Intent): Obj {
  return {
    hero_id: i.heroId, unit: i.unit, slot: slotToWasm(i.slot), action: i.action, args: argsToWasm(i.args),
    snapshot_hash: hexToBytes(i.snapshotHash), revision: i.revision, substituted: i.substituted,
  };
}

// ---- legality -----------------------------------------------------------------------------

export function reasonToString(r: unknown): string {
  if (typeof r === 'string') return r;
  if (isObj(r)) { const k = variant(r); const p = r[k]; return `${k}: ${typeof p === 'string' ? p : JSON.stringify(p)}`; }
  return String(r);
}

export function verdictFromWasm(v: unknown): LegalityVerdict {
  const k = variant(v);
  if (k === 'Legal') {
    const p = (payload(v) ?? {}) as Obj;
    const out: LegalityVerdict = { legal: true, cost: num((p['cost'] as Obj | undefined)?.['resource']) };
    if (Array.isArray(p['path'])) out.path = (p['path'] as Obj[]).map(tileOf);
    if (typeof p['note'] === 'string') out.note = p['note'];
    return out;
  }
  return { legal: false, reason: reasonToString(payload(v) ?? k) };
}

// ---- snapshot -----------------------------------------------------------------------------

export interface HeroMeta { shapeMark?: number; owner?: string; classId?: string }

const tileOf = (t: Obj | undefined): Tile => ({ x: num(t?.['x']), y: num(t?.['y']) });

function terrainOf(t: unknown): Terrain {
  const k = variant(t);
  return (['Open', 'Bad', 'Wall', 'Tunnel', 'Water', 'Spawn', 'Door', 'Deep', 'Thicket', 'Chasm', 'Void'].includes(k) ? k : 'Open') as Terrain;
}

/** `fk_grammar::ClassId` by code (docs/104 NC1: the paladin, druid and bard are 5, 6 and 7). */
export const CLASS_NAMES = ['fighter', 'rogue', 'wizard', 'cleric', 'ranger', 'paladin', 'druid', 'bard'] as const;
export const SPECIES_NAMES = ['human', 'dwarf', 'elf', 'halfling', 'orc', 'tiefling', 'dragonborn', 'half-elf', 'gnome', 'birdfolk'] as const;
/** Each class's default appearance (docs/104 NC6: the Paladin M is 9, the Shaman M 6, the commissioned bard 404). */
export const CLASS_FRAMES: Record<string, number> = { fighter: 1, rogue: 2, wizard: 3, cleric: 4, ranger: 5, paladin: 9, druid: 6, bard: 404 };

/** docs/104 NC2: ids 100–199 are granted by a creation choice (`AbilityId::is_granted`), not a class list. */
export const isGrantedAbility = (id: number): boolean => id >= 100 && id <= 199;

/** Class name from the hero's ability list (the snapshot has no class field). */
export function classFromAbilities(abilities: readonly number[]): string | undefined {
  for (const a of abilities) {
    if (a >= 10 && a < 20) return 'fighter';
    if (a >= 20 && a < 30) return 'rogue';
    if (a >= 30 && a < 40) return 'wizard';
    if (a >= 40 && a < 50) return 'cleric';
    if (a >= 50 && a < 60) return 'ranger';
    // docs/104 NC2: the three later classes' lists sit from 200, ten ids each
    if (a >= 200 && a < 210) return 'paladin';
    if (a >= 210 && a < 220) return 'druid';
    if (a >= 220 && a < 230) return 'bard';
  }
  return undefined;
}

function roleOf(side: string, kind: Obj, classId: string | undefined, leader: boolean): string {
  if (side === 'Hero') return classId ? `hero_${classId}` : 'hero_fighter';
  if (leader) return 'hobb';
  const role = variant((payload(kind) as Obj | undefined)?.['role']);
  if (role === 'Screener') return 'goblin_screener';
  if (role === 'Javelin') return 'goblin_javelin';
  if (role === 'Skirmisher') return 'goblin_skirmisher';
  return 'goblin';
}

/** A serde `fk_sim::Board` object (from `snapshot_view().board` or `generate_board().board`) → the client board. */
export function boardFromWasmObj(board: Obj): Snapshot['board'] {
  const w = num(board['w']), h = num(board['h']);
  const doors = new Map<number, boolean>(((board['doors_open'] as Array<[number, boolean]>) ?? []).map(([id, o]) => [num(id), o]));
  const tiles: TileData[] = ((board['tiles'] as Obj[]) ?? []).map((t) => {
    const flags = (t['flags'] as Obj | undefined) ?? {};
    const terrain = terrainOf(t['terrain']);
    const out: TileData = { terrain, elev: num(t['elev']), cover: num(t['cover']), opaque: Boolean(t['opaque']) };
    if (flags['exit']) out.exit = true;
    if (flags['objective']) out.objective = true;
    if (flags['key']) out.key = true;
    if (flags['ice']) out.ice = true;
    if (t['hazard'] !== undefined && t['hazard'] !== null) out.hazard = variant(t['hazard']);
    if (t['trap'] !== undefined && t['trap'] !== null) out.trap = true;
    if (terrain === 'Door') out.doorOpen = doors.get(num(payload(t['terrain']))) ?? false;
    return out;
  });
  const out: Snapshot['board'] = { w, h, tiles, exits: ((board['exits'] as Obj[]) ?? []).map(tileOf), spawn: ((board['spawn_order'] as Obj[]) ?? []).map(tileOf) };
  const env = envFromWasm(board['env']);
  if (env) out.env = env;
  const fallen = ((board['fallen'] as Obj[]) ?? []).map(fallenFromWasm);
  if (fallen.length) out.fallen = fallen;
  // docs/114 MT1: what lies on the squares and hangs over them
  const matter = ((board['matter'] as Obj[]) ?? []).map(patchFromWasm);
  if (matter.length) out.matter = matter;
  return out;
}

/** `fk_sim::matter::Patch` (docs/114 MT1) → `MatterPatch`. */
export function patchFromWasm(p: Obj): MatterPatch {
  const out: MatterPatch = { tile: tileOf(p['tile'] as Obj), fire: num(p['fire']), charge: num(p['charge']) };
  if (p['surface'] !== null && p['surface'] !== undefined) out.surface = String(p['surface']) as SurfaceName;
  if (p['cloud'] !== null && p['cloud'] !== undefined) out.cloud = String(p['cloud']) as CloudName;
  return out;
}

/** A `FixtureKind::Object` fixture (docs/114 MT4) → `ThingView`; anything else → `undefined`. */
export function thingFromWasm(f: Obj): ThingView | undefined {
  if (variant(f['kind']) !== 'Object') return undefined;
  const k = (payload(f['kind']) ?? {}) as Obj;
  return { id: num(f['id']), tile: tileOf(f['tile'] as Obj), kind: String(k['kind']) as ThingKind, lit: Boolean(k['lit']), burn: num(k['burn']), spent: Boolean(k['spent']), name: String(f['name'] ?? '') };
}

/** Every thing on the field (docs/114 MT4), spent ones too (a replay may name them). */
export function thingsFromWasm(room: Obj | null | undefined): ThingView[] {
  if (!room) return [];
  return ((room['fixtures'] as Obj[]) ?? []).flatMap((f) => { const t = thingFromWasm(f); return t ? [t] : []; });
}

/** `fk_sim::Fallen` (docs/49 FU) → `FallenView`. */
export function fallenFromWasm(f: Obj): FallenView {
  return { unit: num(f['unit']), tile: tileOf(f['tile'] as Obj), size: Math.max(1, num(f['size'])), turn: num(f['turn']), dx: num(f['dx']), dy: num(f['dy']) };
}

/** `fk_sim::Environment` → `FieldEnv` (docs/26 F1); `undefined` for the unstamped plain board. */
export function envFromWasm(v: unknown): FieldEnv | undefined {
  const e = v as Obj | undefined;
  if (!e) return undefined;
  const sky = (e['sky'] as Obj | undefined) ?? {};
  const land = String(e['land'] ?? 'Plain');
  if (land === 'Plain') return undefined;
  const out: FieldEnv = { land, weather: String(sky['weather'] ?? 'Clear').toLowerCase(), intensity: num(sky['intensity']), hour: num(sky['hour']), wind: num(sky['wind']) };
  if (sky['season']) out.season = String(sky['season']).toLowerCase();
  return out;
}

/** `fk_sim::AimRange` (docs/48 AR1) → `AimRange`; `null` → undefined. */
export function aimRangeFromWasm(v: unknown): AimRange | undefined {
  const a = v as Obj | null | undefined;
  if (!a) return undefined;
  const whyOf = (w: unknown): AimWhy => (w === 'Blocked' || w === 'TooFar' ? w : 'Clear');
  return {
    unit: num(a['unit']), action: num(a['action']), from: tileOf(a['from'] as Obj), min: num(a['min']), sight: num(a['sight']),
    tiles: ((a['tiles'] as Obj[]) ?? []).map((t) => ({ tile: tileOf(t['tile'] as Obj), why: whyOf(t['why']) })),
  };
}

/** `fk_sim::stands_at` (docs/81 SA3) → `StandAt[]`. */
/** docs/149 MP: the squares a walk takes in each pulse (`route_legs`); `undefined` when it cannot be walked. */
export function routeLegsFromWasm(v: unknown): Tile[][] | undefined {
  if (!Array.isArray(v)) return undefined;
  return v.map((leg: Obj[]) => leg.map((t) => tileOf(t)));
}

export function standsFromWasm(v: unknown): StandAt[] {
  return ((v as Obj[] | null | undefined) ?? []).map((s) => ({ unit: num(s['unit']), tile: tileOf(s['tile'] as Obj), fallen: Boolean(s['fallen']) }));
}

/** `fk_sim::Survey` (docs/26 F8) → `Survey`. */
export function surveyFromWasm(v: unknown): Survey | undefined {
  const s = v as Obj | null | undefined;
  if (!s) return undefined;
  const lineOf = (l: unknown): FieldLine => (l === 'Veil' || l === 'Block' ? l : 'Clear');
  return {
    unit: num(s['unit']), w: num(s['w']), pace: num(s['pace']), budget: num(s['budget']), from: tileOf(s['from'] as Obj),
    reach: ((s['reach'] as Obj[]) ?? []).map((r) => ({ tile: tileOf(r['tile'] as Obj), cost: num(r['cost']), stand: Boolean(r['stand']), entangled: Boolean(r['entangled']), prev: tileOf(r['prev'] as Obj) })),
    zones: ((s['zones'] as Array<[Obj, number]>) ?? []).map(([t, g]) => ({ tile: tileOf(t), grip: num(g) })),
    grips: ((s['grips'] as Obj[]) ?? []).map(tileOf),
    sight: num(s['sight']), missilePenalty: num(s['missile_penalty']),
    ground: ((s['ground'] as number[]) ?? []).map(num),
    legend: ((s['legend'] as Obj[]) ?? []).map((l) => ({
      key: String(l['key']), label: String(l['label']),
      cost: l['cost'] === null || l['cost'] === undefined ? null : num(l['cost']),
      foot: l['foot'] === null || l['foot'] === undefined ? null : num(l['foot']),
      line: lineOf(l['line']), cover: num(l['cover']),
    })),
    land: String(s['land'] ?? 'Plain'),
    conditions: ((s['conditions'] as string[]) ?? []).map(String),
    traits: ((s['traits'] as string[]) ?? []).map(String),
    modifiers: ((s['modifiers'] as Obj[]) ?? []).map(modifierNoteFromWasm),
    windShift: (((s['wind_shift'] as number[]) ?? [0, 0, 0]).map(num).concat([0, 0, 0]).slice(0, 3)) as [number, number, number],
    // docs/93 AQ10
    ...(s['gait'] !== undefined ? { gait: String(s['gait']), gaitKey: String(s['gait_key'] ?? ''), gaitWords: String(s['gait_words'] ?? '') } : {}),
    submerged: Boolean(s['submerged']), floundering: Boolean(s['floundering']),
  };
}

/** `fk_sim::ModifierNote` (docs/29). */
export function modifierNoteFromWasm(m: Obj): ModifierNote {
  return { modifier: String(m['modifier']), key: String(m['key']), name: String(m['name']), short: String(m['short'] ?? ''), rule: String(m['rule']), why: String(m['why']), toward: num(m['toward']) };
}

/** docs/66 RM11: the deck's scheduled, shown cards with what each will do. */
export function pendingFromWasm(deck: Obj | undefined): PendingCard[] {
  if (!deck) return [];
  const cards = new Map(((deck['cards'] as Obj[]) ?? []).map((c) => [num(c['id']), c] as const));
  return ((deck['pending'] as Obj[]) ?? [])
    .filter((p) => p['declared'] !== false)
    .map((p): PendingCard | undefined => {
      const c = cards.get(num(p['card']));
      if (!c) return undefined;
      const kind = variant(c['kind']);
      const k = (payload(c['kind']) ?? {}) as Obj;
      const tiles = (v: unknown): Tile[] => ((v as Obj[] | undefined) ?? []).map(tileOf);
      const out: PendingCard = { card: num(p['card']), firesAt: num(p['fires_at']), name: String(c['name'] ?? ''), kind, tiles: [] };
      if (kind === 'Call') { out.tiles = tiles(k['entry']); out.group = num(k['group']); }
      else if (kind === 'Raise') { out.units = ((k['units'] as unknown[]) ?? []).map(num); out.caster = num(k['caster']); }
      else if (kind === 'TerrainFails' || kind === 'Hazard') out.tiles = tiles(k['tiles']);
      else if (kind === 'Weather') out.tiles = tiles(k['opaque']);
      else if (kind === 'Trap') out.tiles = k['tile'] ? [tileOf(k['tile'] as Obj)] : [];
      return out;
    })
    .filter((x): x is PendingCard => x !== undefined);
}

export function snapshotFromWasm(s: Obj, snapshotHashHex: string, meta: Map<number, HeroMeta> = new Map()): Snapshot {
  const board = boardFromWasmObj(s['board'] as Obj);
  const units = (s['units'] as Obj[]) ?? [];
  const leaderUnit = units.find((u) => u['side'] === 'Enemy' && variant(u['kind']) === 'Enemy' && ((payload(u['kind']) as Obj)['role'] ?? null) === null);
  const leaderId = leaderUnit ? num(leaderUnit['id']) : undefined;
  const bags = bagsFromWasm(s['bags']);
  const out: Snapshot = {
    encounterId: num(s['encounter_id']), pulseIndex: num(s['pulse_index']), snapshotHash: snapshotHashHex, rulesetVersion: num(s['ruleset_version']),
    board,
    eventNames: Object.fromEntries((((s['deck'] as Obj | undefined)?.['cards'] as Obj[] | undefined) ?? []).map((c) => [num(c['id']), String(c['name'] ?? '')]).filter(([, n]) => n)),
    units: units.map((u): UnitState => {
      const side = String(u['side']);
      const kind = u['kind'] as Obj;
      const stats = u['stats'] as Obj;
      const heroId = variant(kind) === 'Hero' ? num((payload(kind) as Obj)['hero_id']) : undefined;
      const abilities = ((stats['abilities'] as number[]) ?? []).map(num);
      // docs/40 IU3: Use Item is offered by the bag, not the class list — for the hand-authored
      // consumables; docs/72 IT18: a catalogue piece in the bag offers its own ability (10000 + def),
      // aimed and previewed like a spell, once for each kind still unspent
      const bag = bags.find((b) => b.unit === num(u['id']));
      if (bag?.items.some((i) => !i.used && !isCatalogue(i.def))) abilities.push(USE_ITEM_ID);
      for (const i of bag?.items ?? []) {
        if (i.used || !fightUse(catalogueItem(i.def))) continue;
        const id = itemAbilityId(i.def);
        if (!abilities.includes(id)) abilities.push(id);
      }
      const res = stats['resource'] as Obj | undefined;
      const m = heroId !== undefined ? meta.get(heroId) : undefined;
      const classId = m?.classId ?? (heroId !== undefined ? classFromAbilities(abilities) : undefined);
      const leader = leaderId !== undefined && num(u['id']) === leaderId;
      const statuses = (u['statuses'] as Obj[]) ?? [];
      const hp = num(u['hp']);
      const unit: UnitState = {
        unitId: num(u['id']), name: String(u['name']), faction: side === 'Hero' ? 'hero' : side === 'Enemy' ? 'enemy' : 'ally',
        tile: tileOf((u['pos'] as Obj)['tile'] as Obj), hp, hpMax: num(stats['hp_max']), guard: num(stats['guard']), initiative: num(stats['initiative']),
        sprite: roleOf(side, kind, classId, leader), appearance: num(u['appearance']), abilities, onBoard: Boolean(u['on_board']),
        downed: hp <= 0 || statuses.some((st) => st['kind'] === 'Downed'), side: side as UnitState['side'],
      };
      const size = Math.max(1, num(u['size']) || 1);
      if (size > 1) unit.size = size;
      // docs/93 AQ10: the sprite's waterline, hover and the foe card read the gait, never the kit
      const gait = (stats['mobility'] as Obj | undefined)?.['gait'];
      if (typeof gait === 'string' && gait !== 'Foot') unit.gait = gait;
      if (res) unit.resource = { kind: String(res['kind']), current: num(res['current']), max: num(res['max']) };
      const cds = ((stats['cooldowns'] as Array<[unknown, unknown]> | undefined) ?? []).filter(([, n]) => num(n) > 0);
      if (cds.length) unit.cooldowns = Object.fromEntries(cds.map(([a, n]) => [num(a), num(n)]));
      if (heroId !== undefined) unit.heroId = heroId;
      // what its blows carry (fk_sim `weapon_raw`): the main hand's damage, power and focus after Blessed / Weakened
      const sway = statuses.reduce((n, st) => n + (st['kind'] === 'Blessed' ? num(st['magnitude']) : st['kind'] === 'Weakened' ? -num(st['magnitude']) : 0), 0);
      unit.arms = { weapon: num(stats['weapon_damage']), power: num(stats['power']) + sway, focus: num(stats['focus']) + sway, reach: num(stats['reach']) || 1, range: num(stats['range']), ranged: Boolean(stats['ranged_weapon']) };
      const stuck = statuses.some((st) => st['kind'] === 'Rooted' || st['kind'] === 'Staggered');
      const slowed = statuses.reduce((n, st) => n + (st['kind'] === 'Slowed' ? num(st['magnitude']) : 0), 0);
      unit.move = stuck ? 0 : Math.max(1, num(stats['move_tiles']) + num(u['move_bonus']) - slowed);
      if (classId) unit.classId = classId;
      if (m?.shapeMark !== undefined) unit.shapeMark = m.shapeMark;
      if (leader) unit.leader = true;
      const marks = statuses.map((st) => String(st['kind'])).filter((k): k is UnitMark => k === 'Arriving' || k === 'Captive' || k === 'Raised');
      if (marks.length) unit.marks = marks;
      if (statuses.length) unit.statuses = statuses.map((st) => statusTag(st, num(s['pulse_index'])));
      return unit;
    }),
    order: variant(s['order']), objective: variant(s['objective']), pendingSurrender: s['pending_surrender'] != null, fleeCalled: Boolean(s['flee_called']),
  };
  if (leaderId !== undefined) out.leaderUnit = leaderId;
  const call = s['order_call'] as Obj | null | undefined;
  if (call) out.orderCall = { leader: num(call['leader']), order: variant(call['order']), why: variant(call['why']), ...(call['at'] != null ? { at: num(call['at']) } : {}), line: String(call['line']), pulse: num(call['pulse']) };
  const pending = pendingFromWasm(s['deck'] as Obj | undefined);
  if (pending.length) out.pending = pending;
  const kits = s['kit_set'] as Obj | undefined;
  if (kits) {
    const reserve = ((kits['reserve'] as Obj[] | undefined) ?? []).map((g): ReserveView => {
      const r: ReserveView = { role: variant(g['role']), count: num(g['count']), line: String(g['line'] ?? ''), from: String(g['from'] ?? ''), entry: ((g['entry'] as Obj[]) ?? []).map(tileOf), called: Boolean(g['called']) };
      if (g['kin'] !== null && g['kin'] !== undefined) r.kin = variant(g['kin']);
      return r;
    });
    if (reserve.length) out.reserve = reserve;
    if (num(kits['raisings']) > 0) out.raisings = num(kits['raisings']);
    const boss = (kits['leader'] ?? kits['solo']) as Obj | null | undefined;
    if (boss && boss['phase'] && !s['phase_fired']) out.secondForm = true;
    if (boss && typeof boss['name'] === 'string' && boss['name'].trim()) out.bossName = boss['name'].trim();
  }
  const goals = goalsFromWasm(s['objective'], (s['progress'] as unknown[] | undefined) ?? [], s['room'] as Obj | null | undefined);
  if (goals) out.goals = goals;
  const stakes = String(((s['room'] as Obj | null | undefined)?.['stakes']) ?? '');
  if (stakes) out.stakes = stakes;
  const losses = lossesFromWasm(s['losses']);
  if (losses.length) out.losses = losses;
  const stashes = stashesFromWasm(s['room'] as Obj | null | undefined);
  if (stashes.length) out.stashes = stashes;
  const fixtures = fixturesFromWasm(s['room'] as Obj | null | undefined);
  if (fixtures.length) out.fixtures = fixtures;
  // docs/114 MT4: the things on the field
  const things = thingsFromWasm(s['room'] as Obj | null | undefined);
  if (things.length) out.things = things;
  // docs/52: an opened hidden way is a gap through the wall, not a doorway to go into
  for (const f of fixtures) if (f.kind === 'Passage' && f.on) { const d = board.tiles[f.tile.y * board.w + f.tile.x]; if (d) d.way = true; }
  if (bags.length) out.bags = bags;
  const crawl = crawlFromWasm(s['crawl']);
  if (crawl) out.crawl = crawl;
  const explore = exploreFromWasm(s['explore']);
  if (explore) {
    out.explore = explore;
    // docs/61 EX9: a veil's squares are doors only to the rules; the board draws trail and mist
    for (const v of explore.veils) for (const t of v.tiles) { const d = board.tiles[t.y * board.w + t.x]; if (d) d.veil = true; }
    // docs/138 XO5: who has beheld each wonder rides on its fixture, so a click knows whom it still blesses
    for (const w of explore.wonders) { const f = fixtures.find((x) => x.id === w.fixture); if (f) f.blessed = [...w.blessed]; }
  }
  return out;
}

/** docs/61: a wonder's gift (`fk_sim::explore::Gift`). */
export function giftFromWasm(g: unknown): ExploreGift {
  const k = variant(g);
  const p = payload(g);
  switch (k) {
    case 'Marvel': case 'Sight': case 'Lore': case 'Omen': return { kind: k };
    case 'Boon': return { kind: 'Boon', boon: variant(p) as BoonName };
    case 'Path': return { kind: 'Path', veil: num(p) };
    case 'Trove': return { kind: 'Trove', fixture: num(p) };
    case 'Enchant': return { kind: 'Enchant', upgrade: num(p) };
    default: return { kind: 'Unknown' };
  }
}

/** docs/61: `Snapshot.explore` (snake_case serde) → `ExploreView`; `null` (not an exploration) → undefined. */
export function exploreFromWasm(v: unknown): ExploreView | undefined {
  if (!isObj(v)) return undefined;
  const list = (k: string): Obj[] => ((v[k] as Obj[] | undefined) ?? []);
  const tiles = (t: unknown): Tile[] => ((t as Obj[] | undefined) ?? []).map(tileOf);
  const out: ExploreView = {
    air: num(v['air']),
    wonders: list('wonders').map((w) => {
      const o: ExploreView['wonders'][number] = { fixture: num(w['fixture']), name: String(w['name'] ?? ''), look: num(w['look']), text: String(w['text'] ?? ''), gift: giftFromWasm(w['gift']), omen: String(w['omen'] ?? ''), found: Boolean(w['found']), blessed: ((w['blessed'] as unknown[] | undefined) ?? []).map(num) };
      if (w['by'] !== null && w['by'] !== undefined) o.by = num(w['by']);
      return o;
    }),
    veils: list('veils').map((x) => ({ door: num(x['door']), tiles: tiles(x['tiles']), name: String(x['name'] ?? ''), parted: Boolean(x['parted']) })),
    ways: list('ways').map((x) => ({ name: String(x['name'] ?? ''), glimpse: String(x['glimpse'] ?? ''), ending: String(x['ending'] ?? ''), tiles: tiles(x['tiles']), sighted: Boolean(x['sighted']) })),
    ending: String(v['ending'] ?? ''),
    // docs/138 XO9/XO8/XO6: the briefing, the camps and the vaults (absent on a pre-docs/138 board)
    brief: briefFromWasm(v['brief']),
    camps: list('camps').map((c) => ({ lair: num(c['lair']), kind: num(c['kind']), dress: num(c['dress']), name: String(c['name'] ?? ''), idle: String(c['idle'] ?? ''), at: isObj(c['at']) ? tileOf(c['at']) : { x: 0, y: 0 }, tiles: tiles(c['tiles']) })),
    vaults: list('vaults').map((x) => ({ name: String(x['name'] ?? ''), text: String(x['text'] ?? ''), stair: num(x['stair']), chest: num(x['chest']), rule: String(x['rule'] ?? ''), floor: tiles(x['floor']) })),
  };
  if (v['left_by'] !== null && v['left_by'] !== undefined) out.leftBy = num(v['left_by']);
  return out;
}

/** docs/138 XO9: `Explore.brief`; blank words where the vision had none. */
function briefFromWasm(b: unknown): ExploreView['brief'] {
  const o = isObj(b) ? b : {};
  return { why: String(o['why'] ?? '').trim(), history: String(o['history'] ?? '').trim(), ties: String(o['ties'] ?? '').trim() };
}

/** docs/60: `Snapshot.crawl` (snake_case serde) → `CrawlView`; `null` (not a crawl) → undefined. */
export function crawlFromWasm(v: unknown): CrawlView | undefined {
  if (!isObj(v)) return undefined;
  const list = (k: string): Obj[] => ((v[k] as Obj[] | undefined) ?? []);
  const out: CrawlView = {
    name: String(v['name'] ?? ''), hook: String(v['hook'] ?? ''),
    depths: list('depths').map((d) => ({ name: String(d['name'] ?? ''), mood: num(d['mood']), text: String(d['text'] ?? ''), x0: num(d['x0']), y0: num(d['y0']), x1: num(d['x1']), y1: num(d['y1']), entered: Boolean(d['entered']), aside: Boolean(d['aside']) })),
    areas: list('areas').map((a) => ({ name: String(a['name'] ?? ''), depth: num(a['depth']), entered: Boolean(a['entered']), line: String(a['line'] ?? '') })),
    areaOf: Array.from((v['area_of'] as ArrayLike<number> | undefined) ?? [], num),
    lairs: list('lairs').map((l) => ({ name: String(l['name'] ?? ''), area: num(l['area']), units: ((l['units'] as unknown[]) ?? []).map(num), awake: Boolean(l['awake']), line: String(l['line'] ?? '') })),
    roamers: list('roamers').map((r) => ({ unit: num(r['unit']), engaged: Boolean(r['engaged']), line: String(r['line'] ?? '') })),
    lights: list('lights').map((l) => ({ tile: tileOf(l['tile'] as Obj), radius: num(l['radius']), look: num(l['look']) })),
    lore: list('lore').map((l) => ({ fixture: num(l['fixture']), title: String(l['title'] ?? ''), text: String(l['text'] ?? ''), read: Boolean(l['read']) })),
    chutes: list('chutes').map((c) => ({ trap: num(c['trap']), to: tileOf(c['to'] as Obj) })),
    ending: String(v['ending'] ?? ''), pulseLimit: num(v['pulse_limit']),
    snares: list('snares').map(snareFromWasm),
    premise: String(v['premise'] ?? ''), treasure: String(v['treasure'] ?? ''), shrine: String(v['shrine'] ?? ''), quest: String(v['quest'] ?? ''),
  };
  if (v['dweller'] !== null && v['dweller'] !== undefined) out.dweller = num(v['dweller']);
  return out;
}

/** docs/140 DT3: `fk_sim::snare::Trigger` (`"Step"`, `{ Work: "Pull" }`, `{ Crowd: 3 }`) → `SnareTrigger`. */
export function snareTriggerFromWasm(t: unknown): SnareTrigger {
  const kind = (variant(t) || 'Step') as SnareTrigger['kind'];
  const p = payload(t);
  if (kind === 'Work') return { kind, verb: String(p) as InteractVerb };
  if (p !== undefined && p !== null) return { kind, n: num(p) };
  return { kind };
}

/** docs/140 DT2: one `fk_sim::snare::Snare` of `Crawl.snares` (snake_case serde) → `SnareView`. */
export function snareFromWasm(s: Obj): SnareView {
  const opt = (k: string): number | undefined => (s[k] === null || s[k] === undefined ? undefined : num(s[k]));
  const st = s['state'];
  const sv = (s['saved'] as Obj | undefined) ?? {};
  const out: SnareView = {
    id: num(s['id']), def: num(s['def']), trigger: snareTriggerFromWasm(s['trigger']), area: num(s['area']), anchor: tileOf(s['anchor'] as Obj),
    trip: ((s['trip'] as Obj[]) ?? []).map(tileOf), zone: ((s['zone'] as Obj[]) ?? []).map(tileOf),
    lanes: ((s['lanes'] as Array<[Obj, unknown]>) ?? []).map(([t, d]) => ({ tile: tileOf(t), dir: String(d) as Direction })),
    to: ((s['to'] as Obj[]) ?? []).map(tileOf), doors: ((s['doors'] as unknown[]) ?? []).map(num), brood: ((s['brood'] as unknown[]) ?? []).map(num),
    line: String(s['line'] ?? ''), state: (variant(st) || 'Armed') as SnareState, known: Boolean(s['known']), count: num(s['count']),
    progress: ((s['progress'] as unknown[]) ?? []).map(num),
    saved: {
      ground: ((sv['ground'] as Array<[unknown, Obj, unknown]>) ?? []).map(([b, t, g]) => ({ beat: num(b), tile: tileOf(t), terrain: terrainOf(g) })),
      doors: ((sv['doors'] as Array<[unknown, unknown]>) ?? []).map(([f, n]) => ({ fixture: num(f), inputs: num(n) })),
      looks: ((sv['looks'] as Array<[unknown, unknown]>) ?? []).map(([u, l]) => ({ unit: num(u), look: num(l) })),
      at: num(sv['at']), back: Boolean(sv['back']), struck: ((sv['struck'] as unknown[]) ?? []).map(num),
    },
  };
  if (out.state === 'Running') out.since = num((payload(st) as Obj | undefined)?.['since']);
  const fx = opt('fixture'); if (fx !== undefined) out.fixture = fx;
  const stop = opt('stop'); if (stop !== undefined) out.stop = stop;
  const foe = opt('foe'); if (foe !== undefined) out.foe = foe;
  const victim = opt('victim'); if (victim !== undefined) out.victim = victim;
  if (sv['thing'] !== null && sv['thing'] !== undefined) out.saved.thing = num(sv['thing']);
  return out;
}

/**
 * docs/50 SB2: `Snapshot.objective` (an `Objective` tree, its nodes counted in preorder in
 * `progress`) as the aims the HUD lists. A hold reads its count from `progress`, a task its work
 * from the room.
 */
export function goalsFromWasm(o: unknown, progress: unknown[], room: Obj | null | undefined): GoalsView | undefined {
  if (o === null || o === undefined) return undefined;
  let ix = 0;
  const fixtures = ((room?.['fixtures'] as Obj[] | undefined) ?? []);
  const leaf = (v: unknown): GoalView[] => {
    const me = ix++;
    const k = variant(v), p = (payload(v) ?? {}) as Obj;
    switch (k) {
      case 'Any': case 'All': return ((payload(v) as unknown[]) ?? []).flatMap(leaf);
      case 'DefeatAll': return [{ kind: 'Rout' }];
      case 'DefeatLeader': return [{ kind: 'BreakLeader' }];
      case 'HoldTiles': return [{ kind: 'Hold', tiles: ((p['tiles'] as Obj[]) ?? []).map(tileOf), value: num(progress[me] ?? 0), of: num(p['pulses']) }];
      case 'ReachTiles': return [{ kind: 'Reach', tiles: ((p['tiles'] as Obj[]) ?? []).map(tileOf) }];
      case 'Escort': return [{ kind: 'Escort', unit: num(p['unit']), tiles: ((p['to'] as Obj[]) ?? []).map(tileOf) }];
      case 'Survive': return [{ kind: 'Survive', of: num(p['pulses']) }];
      case 'Solve': return [{ kind: 'Solve' }];
      // docs/61 EX3: every standing hero on one of the ways out
      case 'Depart': return [{ kind: 'Depart' }];
      // docs/138 XO1: an exploration is won once its wonder is found
      case 'Find': return [{ kind: 'Find' }];
      case 'Take': return [{ kind: 'Take', fixture: num(p['fixture']) }];
      case 'Deliver': return [{ kind: 'Deliver', fixture: num(p['fixture']), tiles: ((p['to'] as Obj[]) ?? []).map(tileOf) }];
      case 'Work': {
        const f = fixtures.find((x) => num(x['id']) === num(p['fixture']));
        const t = (f ? payload(f['kind']) : undefined) as Obj | undefined;
        return [{ kind: 'Work', fixture: num(p['fixture']), value: num(t?.['done'] ?? 0), of: num(t?.['need'] ?? 0) }];
      }
      default: return [];
    }
  };
  const top = variant(o);
  const goals = leaf(o);
  return { join: top === 'Any' ? 'any' : top === 'All' ? 'all' : 'one', goals };
}

/** docs/50 SB2: `Snapshot.losses`. */
export function lossesFromWasm(v: unknown): LossView[] {
  return ((v as unknown[] | undefined) ?? []).flatMap((l): LossView[] => {
    const k = variant(l), p = payload(l);
    if (k === 'Dropped') return [{ kind: 'Dropped', unit: num(p) }];
    if (k === 'After') return [{ kind: 'After', pulse: num(p) }];
    return [];
  });
}

/** Use Item's ability id (`fk_grammar::AbilityId::USE_ITEM`). */
export const USE_ITEM_ID = 6;

/** `Snapshot.bags` (docs/40 IU1, `fk_sim::Bag`) → `BagView[]`. */
export function bagsFromWasm(v: unknown): BagView[] {
  return ((v as Obj[] | undefined) ?? []).map((b) => ({
    unit: num(b['unit']),
    items: ((b['items'] as Obj[]) ?? []).map((i) => ({ item: num(i['item']), def: num(i['def']), used: Boolean(i['used']) })),
  }));
}

/** The room's `Stash` fixtures (docs/25 D3), hidden ones included; the board draws only found ones. */
export function stashesFromWasm(room: Obj | null | undefined): StashView[] {
  if (!room) return [];
  return ((room['fixtures'] as Obj[]) ?? []).flatMap((f): StashView[] => {
    // docs/126: a fallen foe's pouch draws as a stash of its own kind; `opened` once it is taken
    if (variant(f['kind']) === 'Pouch') {
      const p = payload(f['kind']) as Obj;
      return [{ id: num(f['id']), tile: tileOf(f['tile'] as Obj), kind: 'Pouch', hidden: false, opened: Boolean(p['taken']), gold: num(p['gold']), draws: ((p['items'] as unknown[]) ?? []).length, name: String(f['name'] ?? 'Pouch') }];
    }
    // docs/138 XO7: an exploration's find draws as a stash too: hidden until shown, `opened` once taken
    if (variant(f['kind']) === 'Find') {
      const p = payload(f['kind']) as Obj;
      const items = ((p['items'] as unknown[]) ?? []).map(num);
      return [{ id: num(f['id']), tile: tileOf(f['tile'] as Obj), kind: 'Find', hidden: Boolean(p['hidden']), opened: Boolean(p['taken']), gold: num(p['gold']), draws: items.length, name: String(f['name'] ?? ''), look: num(p['look']), items }];
    }
    if (variant(f['kind']) !== 'Stash') return [];
    const k = payload(f['kind']) as Obj;
    const kind = String(k['kind']);
    return [{
      id: num(f['id']), tile: tileOf(f['tile'] as Obj), kind: (kind === 'Cache' || kind === 'WarChest' ? kind : 'Chest') as StashKind,
      hidden: Boolean(k['hidden']), opened: Boolean(k['opened']), gold: num(k['gold']), draws: num(k['draws']), name: String(f['name'] ?? ''),
    }];
  });
}

/** docs/41: the room's mechanisms, everything but stashes (`FixtureKind` → `FixtureView`). */
export function fixturesFromWasm(room: Obj | null | undefined): FixtureView[] {
  if (!room) return [];
  return ((room['fixtures'] as Obj[]) ?? []).flatMap((f): FixtureView[] => {
    const kind = variant(f['kind']);
    if (kind === 'Stash' || kind === 'Pouch' || kind === 'Find' || !kind) return [];
    const k = (payload(f['kind']) ?? {}) as Obj;
    const base = { id: num(f['id']), tile: tileOf(f['tile'] as Obj), name: String(f['name'] ?? ''), drives: ((f['drives'] as unknown[]) ?? []).map(num) };
    switch (kind) {
      case 'Lever': return [{ ...base, kind, on: Boolean(k['on']) }];
      case 'PressurePlate': return [{ ...base, kind, on: Boolean(k['pressed']), threshold: num(k['threshold']) }];
      case 'Door': case 'Gate': return [{ ...base, kind, on: Boolean(k['open']), door: num(k['door']), needed: num(k['inputs_needed'] ?? k['inputsNeeded']) }];
      case 'Block': return [{ ...base, kind, on: false, weight: num(k['weight']) }];
      case 'Statue': return [{ ...base, kind, on: false }];
      case 'Rune': return [{ ...base, kind, on: Boolean(k['lit']) }];
      case 'Inscription': return [{ ...base, kind, on: Boolean(k['read']) }];
      case 'Lock': {
        // docs/108: a machine lock reads `{ Machine: "Stack" }`
        const lk = k['kind'];
        const out: FixtureView = { ...base, kind, on: Boolean(k['solved']), lock: variant(lk) as LockKindName, inputs: ((k['inputs'] as unknown[]) ?? []).map(num), progress: num(k['progress']) };
        if (out.lock === 'Machine') out.machine = String(payload(lk)) as MachineName;
        return [out];
      }
      case 'Token': return [{ ...base, kind, on: Boolean(k['lit']), look: num(k['look']), value: num(k['value']), state: num(k['state']) }];
      // docs/52: the party's view carries only found ways and clues, and traps as their guise
      case 'Passage': { const out: FixtureView = { ...base, kind, on: Boolean(k['open']), door: num(k['door']) }; const n = optTile(k['nook']); if (n) out.nook = n; return k['found'] ? [out] : []; }
      case 'Clue': { const out: FixtureView = { ...base, kind, on: true }; if (k['points'] !== null && k['points'] !== undefined) out.points = num(k['points']); return k['found'] ? [out] : []; }
      case 'Trap': return [{ ...base, kind, on: false, trap: String(k['kind']) as TrapKindName, armed: Boolean(k['armed']), known: Boolean(k['known']) }];
      // docs/55
      case 'Mirror': return [{ ...base, kind, on: Boolean(k['slash']) }];
      case 'Beacon': return [{ ...base, kind, on: Boolean(k['shining']), dir: String(k['dir']) as Direction, beam: ((k['beam'] as Obj[]) ?? []).map(tileOf) }];
      case 'Eye': case 'Brazier': return [{ ...base, kind, on: Boolean(k['lit']) }];
      case 'Spring': case 'Sunk': return [{ ...base, kind, on: true }];
      case 'Channel': return [{ ...base, kind, on: Boolean(k['wet']) }];
      case 'Valve': return [{ ...base, kind, on: Boolean(k['open']) }];
      case 'Wheel': return [{ ...base, kind, on: Boolean(k['turning']) }];
      case 'Key': { const h = k['holder']; const out: FixtureView = { ...base, kind, on: Boolean(k['placed']) }; if (h !== null && h !== undefined) out.holder = num(h); return [out]; }
      case 'Socket': return [{ ...base, kind, on: Boolean(k['filled']), partner: num(k['key']) }];
      case 'Portal': return [{ ...base, kind, on: Boolean(k['active']), partner: num(k['partner']) }];
      case 'Bell': return [{ ...base, kind, on: Boolean(k['rung']) }];
      case 'Dial': case 'Turntable': return [{ ...base, kind, on: false, face: num(k['face']), faces: num(k['faces']) }];
      // docs/106: a lock book's dial is a dial that carries its look
      case 'BookDial': return [{ ...base, kind: 'Dial', on: false, face: num(k['face']), faces: num(k['faces']), look: num(k['look']) }];
      // docs/105: a bearer and what it holds; a thing to give, lying, carried or held
      case 'Bearer': { const h = k['holds']; const out: FixtureView = { ...base, kind, on: h !== null && h !== undefined, look: num(k['look']) }; if (out.on) out.partner = num(h); return [out]; }
      // docs/106: the lock book's pieces
      case 'Prop': return [{ ...base, kind, on: Boolean(k['lit']), look: num(k['look']) }];
      case 'Switch': return [{ ...base, kind, on: Boolean(k['on']), look: num(k['look']) }];
      case 'Seat': return [{ ...base, kind, on: false, look: num(k['look']) }];
      case 'Jug': return [{ ...base, kind, on: Boolean(k['lit']), cap: num(k['cap']), holds: num(k['holds']) }];
      case 'Idol': { const h = k['holder']; const on = k['on']; const out: FixtureView = { ...base, kind, on: on !== null && on !== undefined }; if (h !== null && h !== undefined) out.holder = num(h); if (out.on) out.partner = num(on); return [out]; }
      case 'Pit': return [{ ...base, kind, on: Boolean(k['filled']) }];
      // docs/50: the relic the stakes are about (held or lying; never while still in its chest), the task (its work so far)
      case 'Relic': { if (k['hidden']) return []; const h = k['holder']; const out: FixtureView = { ...base, kind, on: h !== null && h !== undefined, look: num(k['look']) }; if (out.on) out.holder = num(h); return [out]; }
      case 'Task': return [{ ...base, kind, on: num(k['done']) >= num(k['need']), done: num(k['done']), need: num(k['need']), look: num(k['look']) }];
      case 'Landmark': return [{ ...base, kind, on: false, tiles: ((k['tiles'] as Obj[]) ?? []).map(tileOf) }];
      // docs/60: a stair to another depth, a shrine (`on` once its uses are spent)
      case 'Stair': return [{ ...base, kind, on: false, partner: num(k['partner']), down: Boolean(k['down']) }];
      case 'Shrine': return [{ ...base, kind, on: num(k['uses']) === 0, boon: String(k['boon']) as BoonName, uses: num(k['uses']) }];
      // docs/61: a wonder (`on` once beheld)
      case 'Wonder': return [{ ...base, kind, on: Boolean(k['found']), look: num(k['look']) }];
      // docs/140 DT3: a dungeon trap's curio, in plain sight in its guise (`on` once it has sprung)
      case 'Curio': { const out: FixtureView = { ...base, kind, on: Boolean(k['used']), curio: String(k['look']) as CurioName }; return [out]; }
      // docs/76 EE3: a hidden door, once found (the party's view never carries one before)
      case 'HiddenDoor': {
        if (!k['found']) return [];
        const out: FixtureView = { ...base, kind, on: Boolean(k['open']), door: num(k['door']), room: ((k['room'] as Obj[]) ?? []).map(tileOf) };
        if (k['below'] !== null && k['below'] !== undefined) out.below = num(k['below']);
        return [out];
      }
      default: return [];
    }
  });
}

// ---- projected board ----------------------------------------------------------------------

/** docs/69 ST2: a status on the board, with the turns it still holds as the pulse `pulse` is planned. */
const UNTIL_CLEARED = 4294967295;
function statusTag(st: Obj, pulse: number): StatusTag {
  const tag: StatusTag = { kind: String(st['kind']) as StatusKind, magnitude: num(st['magnitude']) };
  const exp = num(st['expires_after_pulse'] ?? UNTIL_CLEARED);
  if (exp < UNTIL_CLEARED) tag.pulses = Math.max(1, exp - pulse + 1);
  return tag;
}
const statusTags = (v: unknown): StatusTag[] => ((v as Obj[]) ?? []).map((t) => ({ kind: String(t['kind']) as StatusKind, magnitude: num(t['magnitude']) }));
const optTile = (v: unknown): Tile | undefined => (v === null || v === undefined ? undefined : tileOf(v as Obj));

/** `fk_sim::Knockback`: where a shove lands, and the square that stops it short. */
function knockbackOf(v: unknown): { to: Tile; blockedAt?: Tile } | undefined {
  const kb = v as Obj | null | undefined;
  if (!kb) return undefined;
  const out: { to: Tile; blockedAt?: Tile } = { to: tileOf(kb['to'] as Obj) };
  const ba = optTile(kb['blockedAt'] ?? kb['blocked_at']); if (ba) out.blockedAt = ba;
  return out;
}

/** docs/81 SA2: where the hero stands as it acts, when the sim says. */
const fromOf = (e: Obj): { from?: Tile } => { const f = optTile(e['from']); return f ? { from: f } : {}; };

export function effectFromWasm(e: Obj): ProjectedEffect {
  const id = refFromWasm(e['id'] as Obj);
  switch (e['kind']) {
    case 'move': { const out: ProjectedEffect = { kind: 'move', id, path: ((e['path'] as Obj[]) ?? []).map(tileOf), ghost: tileOf(e['ghost'] as Obj) }; const b = optTile(e['blocked']); if (b) out.blocked = b; const f = optTile(e['from']); if (f) out.from = f; return out; }
    case 'attack': {
      const out: ProjectedEffect = { kind: 'attack', id, from: tileOf(e['from'] as Obj), target: num(e['target']), to: tileOf((e['to'] ?? e['from']) as Obj), damage: num(e['damage']), hpAfter: num(e['hp_after'] ?? e['hpAfter']), kill: Boolean(e['kill']), beam: Boolean(e['beam']) };
      const f = e['flank'] as Obj | null | undefined;
      if (f) out.flank = { with: num(f['with']), kind: flankKind(f['kind']) };
      const kb = knockbackOf(e['knockback']); if (kb) out.knockback = kb;
      return out;
    }
    case 'area': return {
      kind: 'area', id, ...fromOf(e), tiles: ((e['tiles'] as Obj[]) ?? []).map(tileOf),
      hits: ((e['hits'] as Obj[]) ?? []).map((h) => {
        const hit: AreaHit = { target: num(h['target']), damage: num(h['damage']), hpAfter: num(h['hpAfter'] ?? h['hp_after']), kill: Boolean(h['kill']), statuses: statusTags(h['statuses']) };
        const at = optTile(h['at']); if (at) hit.at = at;
        const kb = knockbackOf(h['knockback']); if (kb) hit.knockback = kb;
        return hit;
      }),
    };
    case 'support': {
      const out: ProjectedEffect = { kind: 'support', id, ...fromOf(e), glyph: String(e['glyph']).toLowerCase() as never, beneficiaries: ((e['beneficiaries'] as number[]) ?? []).map(num) };
      if (Array.isArray(e['at'])) out.at = (e['at'] as Obj[]).map(tileOf);
      const amount = e['amount']; if (amount !== null && amount !== undefined) out.amount = num(amount);
      const t = optTile(e['on_tile'] ?? e['onTile']); if (t) out.onTile = t;
      return out;
    }
    case 'puzzle': {
      const out: ProjectedEffect = { kind: 'puzzle', id, ...fromOf(e), glyph: String(e['glyph']).toLowerCase() as never, tiles: ((e['tiles'] as Obj[]) ?? []).map(tileOf) };
      if (e['value'] !== null && e['value'] !== undefined) out.value = num(e['value']);
      if (e['lit'] !== null && e['lit'] !== undefined) out.lit = Boolean(e['lit']);
      return out;
    }
    default: return { kind: 'neutral', id, ...fromOf(e), substituted: Boolean(e['substituted']) };
  }
}

const noteLine = (o: Obj): NoteLine => ({ name: String(o['name'] ?? ''), line: String(o['line'] ?? '') });

/** `fk_sim::mind::MindNote` (docs/30): every word is the sim's. */
export function mindFromWasm(m: Obj): MindNote {
  const out: MindNote = {
    unit: num(m['unit']), name: String(m['name'] ?? ''), kin: String(m['kin'] ?? ''), doctrine: String(m['doctrine'] ?? ''),
    wits: noteLine(m['wits'] as Obj), mood: noteLine(m['mood'] as Obj), nerve: num(m['nerve']), prefers: String(m['prefers'] ?? ''),
    quirks: ((m['quirks'] as Obj[]) ?? []).map(noteLine), gear: ((m['gear'] as Obj[]) ?? []).map(noteLine),
    deck: ((m['deck'] as Obj[]) ?? []).map((c): CardCount => ({ name: String(c['name']), line: String(c['line']), family: String(c['family']), count: num(c['count']), from: String(c['from'] ?? '') })),
    drawLeft: num(m['drawLeft']), situations: ((m['situations'] as Obj[]) ?? []).map(noteLine),
    prowl: ((m['prowl'] as Obj[]) ?? []).map((c): CardCount => ({ name: String(c['name']), line: String(c['line']), family: String(c['family']), count: num(c['count']), from: String(c['from'] ?? '') })),
    senses: ((m['senses'] as string[]) ?? []).map(String),
    knows: ((m['knows'] as Obj[]) ?? []).map(noteLine),
  };
  if (m['insight']) out.insight = noteLine(m['insight'] as Obj);
  if (m['part']) out.part = noteLine(m['part'] as Obj);
  if (m['last']) out.last = noteLine(m['last'] as Obj);
  if (m['grudge'] !== null && m['grudge'] !== undefined) out.grudge = num(m['grudge']);
  if (m['aware']) out.aware = noteLine(m['aware'] as Obj);
  if (m['focus']) out.focus = focusFromWasm(m['focus'] as Obj);
  return out;
}

/** `fk_sim::mind::focus::FocusNote` (docs/32). */
function focusFromWasm(f: Obj): FocusNote {
  const out: FocusNote = {
    score: num(f['score']), name: String(f['name'] ?? ''), line: String(f['line'] ?? ''),
    parts: ((f['parts'] as Obj[]) ?? []).map((p): FocusPart => ({ name: String(p['name'] ?? ''), value: num(p['value']) })),
  };
  const i = f['intent'] as Obj | null | undefined;
  if (i) {
    out.intent = { card: String(i['card'] ?? ''), line: String(i['line'] ?? ''), pulse: num(i['pulse']), of: num(i['of']), hold: String(i['hold'] ?? '') };
    if (i['at'] !== null && i['at'] !== undefined) out.intent.at = num(i['at']);
  }
  if (f['lapse']) out.lapse = noteLine(f['lapse'] as Obj);
  return out;
}

/** wasm `fog_view` (camelCase, `fk_sim::fog::FogView`) → `FogView`; `null` (clear field) → undefined. */
export function fogFromWasm(f: unknown): FogView | undefined {
  if (!f || typeof f !== 'object') return undefined;
  const o = f as Obj;
  const sense = (s: unknown): FogMark['sense'] => (s === 'Heard' ? 'heard' : s === 'Smelled' ? 'smelled' : 'seen');
  return {
    w: num(o['w']), h: num(o['h']),
    veils: ((o['veils'] as Obj[]) ?? []).map((v) => ({ key: String(v['key']) as FogVeil['key'], name: String(v['name']), line: String(v['line']) })),
    cells: ((o['cells'] as number[]) ?? []).map((c) => (c === 2 ? 2 : c === 1 ? 1 : 0)),
    lit: ((o['lit'] as number[]) ?? []).map((c) => (c ? 1 : 0)),
    eyes: ((o['eyes'] as Obj[]) ?? []).map((e): FogEyes => {
      const s = (e['senses'] as Obj) ?? {};
      return {
        unit: num(e['unit']), sight: num(s['sight']), dark: num(s['dark']), darkvision: Boolean(s['darkvision']), keen: Boolean(s['keen']),
        light: num(s['light']), scent: num(s['scent']), tiles: ((e['tiles'] as number[]) ?? []).map(Number), words: ((e['words'] as string[]) ?? []).map(String),
      };
    }),
    marks: ((o['marks'] as Obj[]) ?? []).map((m) => ({ unit: num(m['unit']), tile: tileOf(m['tile'] as Obj), pulse: num(m['pulse']), sense: sense(m['sense']), look: num(m['look']) })),
    pulse: num(o['pulse']),
  };
}

function aimFromWasm(a: unknown): Aim {
  const k = variant(a);
  const p = payload(a) as Obj | undefined;
  switch (k) {
    case 'Unit': return { kind: 'unit', target: num(p?.['target']) };
    case 'Tile': return { kind: 'tile', tile: tileOf(p) };
    case 'Region': {
      const c = tileOf(p?.['centre'] as Obj), r = num(p?.['radius']);
      const tiles: Tile[] = [];
      // the sim's Burst: a diamond of squares within r steps (docs/39 FW3)
      for (let y = c.y - r; y <= c.y + r; y++) for (let x = c.x - r; x <= c.x + r; x++) if (Math.abs(x - c.x) + Math.abs(y - c.y) <= r) tiles.push({ x, y });
      return { kind: 'region', tiles };
    }
    default: return { kind: 'none' };
  }
}

const snake = (s: string): string => s.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

export function declarationFromWasm(d: Obj, leaderUnit?: number, standingOrder?: string): EnemyDeclaration {
  const actor = num(d['actor']);
  const out: EnemyDeclaration = {
    actor, telegraph: String(d['telegraph']).toLowerCase() as never, actionName: String(d['actionName'] ?? d['action_name'] ?? ''),
    aim: aimFromWasm(d['aim']), projected: ((d['projected'] as Array<[number, number]>) ?? []).map(([u, n]) => [num(u), num(n)] as [number, number]),
    held: Boolean(d['held']), stage: String(d['stage']) as Stage, path: ((d['path'] as Obj[]) ?? []).map(tileOf),
  };
  const ghost = optTile(d['ghost']); if (ghost) out.ghost = ghost;
  if (Array.isArray(d['region'])) out.region = (d['region'] as Obj[]).map(tileOf);
  if (d['stance']) out.stance = snake(variant(d['stance'])) as never;
  if (Array.isArray(d['retargeted'])) out.retargeted = [num((d['retargeted'] as number[])[0]), num((d['retargeted'] as number[])[1])];
  const t = d['tactic'] as Obj | null | undefined;
  if (t) out.tactic = noteLine(t);
  if (leaderUnit !== undefined && actor === leaderUnit) { out.leader = true; if (standingOrder) out.standingOrder = standingOrder; }
  return out;
}

export function boardFromWasm(pb: Obj, snapshot?: Snapshot, owners: Map<number, string> = new Map()): ProjectedBoard {
  const units = ((pb['units'] as Obj[]) ?? []).map((u): ProjectedUnit => {
    const out: ProjectedUnit = {
      unitId: num(u['unit'] ?? u['unitId']), start: tileOf(u['start'] as Obj), end: tileOf(u['end'] as Obj),
      hpStart: num(u['hpStart']), hpEnd: num(u['hpEnd']), hpMax: num(u['hpMax']), dead: Boolean(u['dead']), revived: Boolean(u['revived']),
      statusesStart: statusTags(u['statusesStart']), statusesEnd: statusTags(u['statusesEnd']), side: String(u['side']) as never, name: String(u['name'] ?? ''), appearance: num(u['appearance']), onBoardEnd: Boolean(u['onBoardEnd']),
    };
    const hero = u['heroId'] ?? u['hero_id'];
    if (hero !== null && hero !== undefined) { out.heroId = num(hero); const o = owners.get(out.heroId); if (o) out.owner = o; }
    return out;
  });
  const conflicts = ((pb['conflicts'] as Obj[]) ?? []).map((c): ConflictFlag => {
    const out: ConflictFlag = { severity: String(c['severity']).toLowerCase() as never, intent: refFromWasm(c['intent'] as Obj), reason: String(c['reason']) as never };
    const at = optTile(c['at']); if (at) out.at = at;
    if (typeof c['detail'] === 'string') out.detail = c['detail'];
    return out;
  });
  return {
    snapshotHash: String(pb['snapshotHashHex'] ?? bytesToHex(pb['snapshotHash'] as number[])), rulesetVersion: num(pb['rulesetVersion']),
    planHash: String(pb['planHashHex'] ?? bytesToHex(pb['planHash'] as number[])),
    units, effects: ((pb['effects'] as Obj[]) ?? []).map(effectFromWasm), conflicts,
    ordering: ((pb['ordering'] as Obj[]) ?? []).map((o) => ({ stage: String(o['stage']) as Stage, first: refFromWasm(o['first'] as Obj), then: refFromWasm(o['then'] as Obj), why: String(o['why']) })),
    declarations: ((pb['declarations'] as Obj[]) ?? []).map((d) => declarationFromWasm(d, snapshot?.leaderUnit, snapshot?.order)),
    hints: ((pb['hints'] as Obj[]) ?? []).map((h) => ({ rung: Math.max(1, Math.min(4, num(h['rung']))) as 1 | 2 | 3 | 4, tiles: ((h['tiles'] as Obj[]) ?? []).map(tileOf) })),
    wings: ((pb['wings'] as Obj[]) ?? []).map((w) => ({ wing: num(w['wing']), bounds: ((w['bounds'] as Obj[]) ?? []).map(tileOf), needs: String(w['slice'] ?? ''), staffed: Boolean(w['staffed']) })),
    ...(pb['ended'] !== null && pb['ended'] !== undefined ? { ended: variant(pb['ended']) } : {}),
    swayed: ((pb['swayed'] as Obj[]) ?? []).map((n): SwayNote => {
      const out: SwayNote = { unit: num(n['unit']), modifier: String(n['modifier']), delta: num(n['delta']) };
      if (n['heroId'] !== null && n['heroId'] !== undefined) out.heroId = num(n['heroId']);
      if (n['slot'] !== null && n['slot'] !== undefined) out.slot = slotFromWasm(n['slot']);
      if (n['actor'] !== null && n['actor'] !== undefined) out.actor = num(n['actor']);
      return out;
    }),
  };
}

// ---- resolution log -----------------------------------------------------------------------

function sourceGroup(s: unknown): DamageSourceGroup {
  switch (variant(s)) {
    case 'Melee': case 'Push': return 'melee';
    case 'Ranged': return 'ranged';
    case 'Spell': case 'Area': return 'blast';
    default: return 'tick';
  }
}

export function logEffectFromWasm(e: unknown): Effect {
  const k = variant(e);
  const p = (payload(e) ?? {}) as Obj;
  const tiles = (v: unknown): Tile[] => ((v as Obj[]) ?? []).map(tileOf);
  switch (k) {
    case 'Moved': { const out: Effect = { kind: 'Moved', unit: num(p['unit']), path: tiles(p['path']) }; const b = optTile(p['blocked_at']); if (b) out.blockedAt = b; const c = (p['costs'] as number[] | undefined) ?? []; if (c.length) out.costs = c.map(num); const f = optTile(p['from']); if (f) out.from = f; return out; }
    case 'Damaged': return { kind: 'Damaged', unit: num(p['unit']), raw: num(p['raw']), absorbed: num(p['absorbed']), dealt: num(p['dealt']), hpAfter: num(p['hp_after']), source: sourceGroup(p['source']) };
    case 'Healed': return { kind: 'Healed', unit: num(p['unit']), amount: num(p['amount']), hpAfter: num(p['hp_after']) };
    case 'Displaced': { const out: Effect = { kind: 'Displaced', unit: num(p['unit']), from: tileOf(p['from'] as Obj), to: tileOf(p['to'] as Obj) }; const b = optTile(p['blocked_at']); if (b) out.blockedAt = b; return out; }
    case 'StatusSet': return { kind: 'StatusSet', unit: num(p['unit']), status: String(p['kind']) as StatusKind, magnitude: num(p['magnitude']), expiresAfter: num(p['expires_after']) };
    case 'StatusCleared': return { kind: 'StatusCleared', unit: num(p['unit']), status: String(p['kind']) as StatusKind };
    case 'Dropped': { const out: Effect = { kind: 'Dropped', unit: num(p['unit']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); if (isObj(p['lies'])) out.lies = fallenFromWasm(p['lies']); return out; }
    case 'Revived': return { kind: 'Revived', unit: num(p['unit']), hp: num(p['hp']) };
    case 'Spawned': return { kind: 'Spawned', unit: num(p['unit']), tile: tileOf(p['tile'] as Obj) };
    case 'Held': return { kind: 'Held', unit: num(p['unit']), reason: variant(p['reason']) };
    case 'EventFired': return { kind: 'EventFired', card: num(p['card']), tiles: tiles(p['tiles']), title: `Event ${num(p['card'])}` };
    case 'Interacted': {
      // docs/41: a lock's verdict, an inscription read, a block moved — what the playback shows
      const out: Effect = { kind: 'Interacted', fixture: num(p['fixture']), tiles: [], verb: String(p['verb']) as InteractVerb };
      const r = p['result'];
      const rk = variant(r), rp = (payload(r) ?? {}) as Obj;
      if (rk === 'Judged') {
        out.verdict = variant(rp['verdict']) as never;
        // docs/105: a Tally says how many stand right
        if (out.verdict === 'Near') out.right = num(((payload(rp['verdict']) ?? {}) as Obj)['right']);
      }
      if (rk === 'Read') out.read = num(rp['clue']);
      if (rk === 'BlockMoved') { out.moved = { from: tileOf(rp['from'] as Obj), to: tileOf(rp['to'] as Obj) }; out.tiles = [out.moved.from, out.moved.to]; }
      if (rk === 'Warped') out.tiles = [tileOf(rp['from'] as Obj), tileOf(rp['to'] as Obj)];
      if (rk === 'StashOpened') out.opened = true;
      if (rk === 'Taken') out.taken = true;
      if (rk === 'Placed') out.placed = num(rp['key']);
      if (rk === 'Beheld') out.beheld = true;
      if (rk === 'Tripped') out.tripped = true;
      return out;
    }
    case 'GearPower': return { kind: 'GearPower', unit: num(p['unit']), power: variant(p['power']), amount: num(p['amount']) };
    case 'Flanked': return { kind: 'Flanked', unit: num(p['unit']), target: num(p['target']), with: num(p['with']), flank: flankKind(p['kind']) };
    case 'Swayed': return { kind: 'Swayed', unit: num(p['unit']), modifier: String(p['modifier']), delta: num(p['delta']) };
    case 'ItemUsed': return { kind: 'ItemUsed', unit: num(p['unit']), item: num(p['item']), def: num(p['def']), target: num(p['target']) };
    case 'ResourceGained': return { kind: 'ResourceGained', unit: num(p['unit']), amount: num(p['amount']), left: num(p['left']) };
    case 'MoveUsed': return { kind: 'MoveUsed', unit: num(p['unit']), id: num(p['id']), name: String(p['name'] ?? '') };
    case 'AbilityUsed': { const out: Effect = { kind: 'Cast', unit: num(p['unit']), ability: num(p['ability']), from: p['from'] ? tileOf(p['from'] as Obj) : { x: 0, y: 0 }, tiles: tiles(p['tiles']) }; const a = optTile(p['aim']); if (a) out.aim = a; return out; }
    case 'TerrainSet': return { kind: 'TerrainSet', terrain: terrainOf(p['terrain']), tiles: tiles(p['tiles']) };
    // docs/104: the paladin's intercept and the bard's Encore
    case 'Interposed': return { kind: 'Interposed', unit: num(p['unit']), ward: num(p['ward']) };
    case 'Hastened': return { kind: 'Hastened', unit: num(p['unit']), turns: num(p['turns']) };
    case 'TrapSet': return { kind: 'Other', name: 'TrapSet', unit: num(p['owner']), tiles: [tileOf(p['tile'] as Obj)] };
    case 'Searched': return { kind: 'Searched', unit: num(p['unit']), tiles: tiles(p['tiles']), found: num(p['found']) };
    case 'Revealed': { const t = tileOf(p['tile'] as Obj); const out: Effect = { kind: 'Revealed', fixture: num(p['fixture']), tile: t, tiles: [t] }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    case 'TrapSprung': return { kind: 'TrapSprung', unit: num(p['unit']), fixture: num(p['fixture']), tile: tileOf(p['tile'] as Obj), trap: String(p['kind']) as TrapKindName };
    case 'Disarmed': return { kind: 'Disarmed', unit: num(p['unit']), fixture: num(p['fixture']), tile: tileOf(p['tile'] as Obj), trap: String(p['kind']) as TrapKindName };
    // docs/60: the dungeon crawl
    case 'LairWoke': { const out: Effect = { kind: 'LairWoke', lair: num(p['lair']), units: ((p['units'] as unknown[]) ?? []).map(num), why: variant(p['why']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    case 'Roused': { const out: Effect = { kind: 'Roused', unit: num(p['unit']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    case 'AreaEntered': { const out: Effect = { kind: 'AreaEntered', unit: num(p['unit']), area: num(p['area']) }; if (p['depth'] !== null && p['depth'] !== undefined) out.depth = num(p['depth']); return out; }
    case 'LoreRead': return { kind: 'LoreRead', unit: num(p['unit']), lore: num(p['lore']) };
    case 'ShrineAnswered': return { kind: 'ShrineAnswered', unit: num(p['unit']), fixture: num(p['fixture']), boon: variant(p['boon']) as BoonName, touched: ((p['touched'] as unknown[]) ?? []).map(num) };
    case 'Fell': return { kind: 'Fell', unit: num(p['unit']), from: tileOf(p['from'] as Obj), to: tileOf(p['to'] as Obj) };
    // docs/61: exploration
    case 'Discovered': return { kind: 'Discovered', unit: num(p['unit']), wonder: num(p['wonder']) };
    case 'Blessed': return { kind: 'Blessed', unit: num(p['unit']), wonder: num(p['wonder']) };
    case 'VeilParted': { const out: Effect = { kind: 'VeilParted', veil: num(p['veil']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    case 'Glimpsed': return { kind: 'Glimpsed', unit: num(p['unit']), at: tileOf(p['at'] as Obj), cells: num(p['cells']) };
    case 'WaySighted': return { kind: 'WaySighted', way: num(p['way']), by: num(p['by']) };
    case 'Departed': return { kind: 'Departed', way: num(p['way']) };
    // docs/66 RM10
    case 'Called': return { kind: 'Called', leader: num(p['leader']), group: num(p['group']), line: String(p['line'] ?? ''), units: ((p['units'] as unknown[]) ?? []).map(num) };
    case 'CallBroken': return { kind: 'CallBroken', leader: num(p['leader']), group: num(p['group']) };
    case 'Raised': return { kind: 'Raised', caster: num(p['caster']), units: ((p['units'] as unknown[]) ?? []).map(num) };
    case 'RaiseBroken': return { kind: 'RaiseBroken', caster: num(p['caster']) };
    case 'CaptiveFreed': { const out: Effect = { kind: 'CaptiveFreed', unit: num(p['unit']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    // docs/118 PT14: the retreat shouted, a foe running off the field
    case 'Retreat': { const out: Effect = { kind: 'Retreat', line: String(p['line'] ?? ''), units: ((p['units'] as unknown[]) ?? []).map(num) }; if (p['leader'] !== null && p['leader'] !== undefined) out.leader = num(p['leader']); return out; }
    case 'Left': { const out: Effect = { kind: 'Left', unit: num(p['unit']), from: tileOf(p['from'] as Obj), path: tiles(p['path']) }; const c = (p['costs'] as number[] | undefined) ?? []; if (c.length) out.costs = c.map(num); return out; }
    // docs/114 MT3: a row of the interaction table fired
    case 'Reacted': {
      const set = ((p['set'] as Obj[]) ?? []).map(patchFromWasm);
      const things = ((p['things'] as Obj[]) ?? []).flatMap((f) => { const t = thingFromWasm(f); return t ? [t] : []; });
      const at = tileOf(p['at'] as Obj);
      const out: Effect = { kind: 'Reacted', rule: num(p['rule']), at, set, things, tiles: [at, ...set.map((m) => m.tile), ...things.map((t) => t.tile)] };
      if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']);
      return out;
    }
    // docs/126
    case 'PouchLeft': { const at = tileOf(p['at'] as Obj); return { kind: 'PouchLeft', unit: num(p['unit']), fixture: num(p['fixture']), at, gold: num(p['gold']), items: ((p['items'] as unknown[]) ?? []).map(num), tiles: [at] }; }
    case 'Glanced': { const out: Effect = { kind: 'Glanced', unit: num(p['unit']), landed: num(p['landed']), prevented: num(p['prevented']) }; if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']); return out; }
    case 'PickedUp': return { kind: 'PickedUp', unit: num(p['unit']), fixture: num(p['fixture']), gold: num(p['gold']), items: ((p['items'] as unknown[]) ?? []).map(num) };
    // docs/140: dungeon traps
    case 'SnareSprung': {
      const out: Effect = { kind: 'SnareSprung', snare: num(p['snare']), def: num(p['def']), tile: tileOf(p['tile'] as Obj) };
      if (p['victim'] !== null && p['victim'] !== undefined) out.victim = num(p['victim']);
      if (p['by'] !== null && p['by'] !== undefined) out.by = num(p['by']);
      return out;
    }
    case 'SnareActed': return { kind: 'SnareActed', snare: num(p['snare']), act: String(p['act']) as ActKind, tiles: tiles(p['tiles']), units: ((p['units'] as unknown[]) ?? []).map(num) };
    case 'SnareEnded': return { kind: 'SnareEnded', snare: num(p['snare']) };
    case 'SnareFound': return { kind: 'SnareFound', snare: num(p['snare']), unit: num(p['unit']) };
    case 'SnareDisarmed': return { kind: 'SnareDisarmed', snare: num(p['snare']), unit: num(p['unit']), tile: tileOf(p['tile'] as Obj) };
    default: { const out: Effect = { kind: 'Other', name: k || 'Unknown', tiles: [] }; if (p['unit'] !== undefined) out.unit = num(p['unit']); return out; }
  }
}

export function causeFromWasm(c: unknown): Cause {
  const k = variant(c);
  const p = (payload(c) ?? {}) as Obj;
  switch (k) {
    case 'Intent': return { kind: 'Intent', ref: refFromWasm(p) };
    case 'Declaration': return { kind: 'Declaration', actor: num(p['actor']) };
    case 'Reaction': return { kind: 'Reaction', actor: num(p['actor']), idx: num(p['idx']) };
    case 'Villain': return { kind: 'Villain', leader: num(p['leader']), pulseSlot: num(p['pulse_slot']) };
    case 'Event': return { kind: 'Event', card: num(payload(c)) };
    case 'Snare': return { kind: 'Snare', snare: num(payload(c)) };
    default: return { kind: 'Tick' };
  }
}

export function logFromWasm(l: Obj): ResolutionLog {
  return {
    encounterId: num(l['encounter_id']), pulseIndex: num(l['pulse_index']), rulesetVersion: num(l['ruleset_version']),
    snapshotHash: bytesToHex(l['snapshot_hash'] as number[]), planHash: bytesToHex(l['plan_hash'] as number[]), endSnapshotHash: bytesToHex(l['end_snapshot_hash'] as number[]),
    entries: ((l['entries'] as Obj[]) ?? []).map((e): ResolutionEntry => {
      const out: ResolutionEntry = { seq: num(e['seq']), stage: String(e['stage']) as Stage, cause: causeFromWasm(e['cause']), effect: logEffectFromWasm(e['effect']) };
      if (e['actor'] !== null && e['actor'] !== undefined) out.actor = num(e['actor']);
      return out;
    }),
    unresolved: ((l['unresolved'] as Array<[number, unknown, unknown]>) ?? []).map(([h, s, r]) => [num(h), slotFromWasm(s), reasonToString(r)] as [number, Slot, string]),
    unready: ((l['unready'] as number[]) ?? []).map(num),
  };
}
